// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { Column, H2, LgPadding, Pressable, Row } from '@alto/design-system';
import { AddOnsContext, type AddOnsContextProps } from '@alto/features';
import { useQueryEssentials } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useEssentialsAddress } from '~shared/features/essentials';
import { getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { getURLParams } from '~shared/helpers/helper';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';
import { ErrorPage, GridProductList, LoadingPage, ProductItem } from '~web/features/essentials/components';
import { useTrackViewedEvent } from '~web/features/essentials/hooks';
import { Icon } from '~web/features/essentials/primitives';

type StoreCategoryRouteParams = {
  readonly categoryName: string;
};

/** Matches the back control column so the title stays visually centered (replaces a second hidden chevron). */
const HeaderEndCap = styled.div`
  flex: 0 0 48px;
  width: 48px;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitleCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StoreCategory = () => {
  const { goBack } = useNavigation();
  const { categoryName = '' } = useParams<StoreCategoryRouteParams>();
  const { order, origin } = getURLParams();
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID: Number(order) }));
  const address = useEssentialsAddress({ shipmentID: Number(order) });
  const { isLoading, error, categories } = useQueryEssentials({
    addressID: address?.id,
    date: shipment?.scheduled_date,
  });
  const category = useMemo(
    () => categories.find((category) => category.name === categoryName),
    [categories, categoryName],
  );
  const addOnsContext = useMemo<AddOnsContextProps>(
    () => ({
      shipmentID: shipment?.id,
      origin,
    }),
    [shipment?.id, origin],
  );

  useTrackViewedEvent(EVENTS.ESSENTIALS__STORE_CATEGORY_VIEWED, order, origin);

  if (isLoading) {
    return <LoadingPage />;
  }
  if (error) {
    return (
      <ErrorPage
        title="Unable to load Essentials"
        description={error?.message || 'unknown error'}
      />
    );
  }
  if (!categories || !category) {
    return (
      <ErrorPage
        title="Unable to load Essentials"
        description="missing categories"
      />
    );
  }

  return (
    <AddOnsContext.Provider value={addOnsContext}>
      <Column backgroundColor={COLORS.BACKGROUND_COLORS.WHITE}>
        <LgPadding>
          <Row centerVertically>
            <HeaderEndCap>
              <Pressable
                accessibilityLabel="navigate back"
                backgroundColor={COLORS.BACKGROUND_COLORS.TRANSPARENT}
                hoverBackgroundColor={COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST}
                onPress={goBack}
                pressedBackgroundColor={COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST}
              >
                <Icon
                  interactive
                  label="navigate back"
                  name="chevronleft"
                  size="LG"
                />
              </Pressable>
            </HeaderEndCap>
            <HeaderTitleCell>
              <H2 center>{categoryName}</H2>
            </HeaderTitleCell>
            <HeaderEndCap aria-hidden />
          </Row>
        </LgPadding>
        <GridProductList>
          {category.add_on_otcs.map((product, index) => (
            <ProductItem
              key={product.sku}
              categoryName={categoryName}
              count={1}
              product={product}
              index={index}
              isStoreItem
            />
          ))}
        </GridProductList>
      </Column>
    </AddOnsContext.Provider>
  );
};
